import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { 
  signInWithEmailAndPassword, 
  signInWithPopup, 
  GoogleAuthProvider,
  signInWithCustomToken
} from "firebase/auth";
import { auth } from "@/lib/firebase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Mail, Lock, Loader2, Globe, Sparkles, ArrowRight } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { useAuthStore } from "@/store/authStore";

export default function LoginPage() {
  const { user, loading: authLoading } = useAuthStore();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    // If user is already logged in, go to dashboard
    if (user && !authLoading) {
      navigate("/dashboard");
    }
  }, [user, authLoading, navigate]);

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      console.log("Attempting email login for:", email);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      console.log("Login success:", userCredential.user.uid);
      navigate("/dashboard");
    } catch (err: any) {
      console.error("Login failed:", {
        code: err.code,
        message: err.message,
        fullError: err
      });
      setError(err.message || "Failed to login");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError("");
    try {
      console.log("Attempting Google login...");
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      console.log("Google login success:", result.user.uid);
      navigate("/dashboard");
    } catch (err: any) {
      console.error("Google login failed:", {
        code: err.code,
        message: err.message,
        fullError: err
      });
      setError(err.message || "Failed to login with Google");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-[calc(100vh-10rem)] px-4">
      <Helmet>
        <title>Đăng nhập | Flashcard Platform</title>
      </Helmet>
      
      <div className="w-full max-w-md relative">
        <div className="absolute -top-20 -right-20 w-64 h-64 bg-primary/20 blur-[100px] rounded-full -z-10" />
        <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-violet-500/20 blur-[100px] rounded-full -z-10" />
        
        <Card className="border-2 rounded-[3rem] shadow-2xl overflow-hidden bg-background/80 backdrop-blur-xl">
          <CardHeader className="space-y-4 pt-12 pb-8">
            <div className="mx-auto w-16 h-16 rounded-3xl bg-primary flex items-center justify-center shadow-xl shadow-primary/20 mb-2">
              <Sparkles className="h-8 w-8 text-white fill-current" />
            </div>
            <CardTitle className="text-4xl font-black text-center tracking-tight">Chào mừng!</CardTitle>
            <CardDescription className="text-center text-lg font-medium">
              Đăng nhập để bắt đầu hành trình chinh phục tri thức.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6 px-10 pb-10">
            <form onSubmit={handleEmailLogin} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="font-bold text-xs uppercase tracking-widest text-muted-foreground ml-1">Email của bạn</Label>
                <div className="relative group">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground group-focus-within:text-primary transition-colors" />
                  <Input 
                    id="email" 
                    type="email" 
                    placeholder="name@example.com" 
                    className="pl-12 h-14 rounded-2xl border-2 focus-visible:ring-primary/20 text-lg transition-all"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required 
                  />
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between ml-1">
                  <Label htmlFor="password" className="font-bold text-xs uppercase tracking-widest text-muted-foreground">Mật khẩu</Label>
                  <Link 
                    to="/forgot-password" 
                    className="text-xs font-bold text-primary hover:text-primary/80 transition-colors uppercase tracking-wider"
                  >
                    Quên mật khẩu?
                  </Link>
                </div>
                <div className="relative group">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground group-focus-within:text-primary transition-colors" />
                  <Input 
                    id="password" 
                    type="password" 
                    placeholder="••••••••"
                    className="pl-12 h-14 rounded-2xl border-2 focus-visible:ring-primary/20 text-lg transition-all"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required 
                  />
                </div>
              </div>
              {error && <p className="text-sm font-bold text-destructive px-1">{error}</p>}
              <Button type="submit" className="w-full h-14 rounded-2xl text-lg font-bold shadow-xl shadow-primary/20 group" disabled={loading}>
                {loading ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : null}
                Tiếp tục <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </form>

            <div className="relative py-2">
              <div className="absolute inset-0 flex items-center">
                <Separator className="border-muted-foreground/10" />
              </div>
              <div className="relative flex justify-center text-xs font-bold uppercase tracking-widest">
                <span className="bg-background/50 px-4 text-muted-foreground/60 backdrop-blur-sm">
                  Hoặc đăng nhập với
                </span>
              </div>
            </div>

            <Button variant="outline" className="w-full h-14 rounded-2xl text-lg font-bold border-2 hover:bg-muted/50 transition-all" onClick={handleGoogleLogin} disabled={loading}>
              <Globe className="mr-3 h-5 w-5 text-primary" />
              Google Authentication
            </Button>
          </CardContent>
          <CardFooter className="flex flex-wrap items-center justify-center gap-1.5 pb-12 text-base font-medium text-muted-foreground border-t-2 border-dashed border-muted-foreground/10 pt-8 mt-2">
            Bạn chưa có tài khoản?{" "}
            <Link to="/register" className="text-primary hover:text-primary/80 transition-colors font-black">
              Đăng ký ngay
            </Link>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}


